import {
  AD_FEED_CARD_SIZE_LABEL,
  adSlotSizeLabel,
  applyAdPreviewSession,
  adPreviewFromSearch,
  readAdPreviewSession,
  syncAdPreviewFromSearch,
  type AdSlotSizeKey,
} from "../shared/ad-preview.ts";
import {
  adsServingReady,
  slotForPlacement,
  type AdPlacement,
  type AdsPublicConfig,
} from "../shared/ads.ts";
import type { PublicConfig } from "../shared/types.ts";
import { t } from "./i18n/index.ts";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

let adsConfig: AdsPublicConfig | null = null;
let scriptPromise: Promise<void> | null = null;
const pushed = new WeakSet<HTMLElement>();

function browserSession(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/** Read URL `?adpreview=` and persist in sessionStorage for this tab. */
export function syncAdPreviewFromLocation(search: string = location.search): boolean {
  const storage = browserSession();
  if (!storage) return false;
  return syncAdPreviewFromSearch(search, storage);
}

export function readAdPreviewEnabled(): boolean {
  const storage = browserSession();
  if (!storage) return false;
  return readAdPreviewSession(storage);
}

export function readAdsConfig(): AdsPublicConfig | null {
  return adsConfig;
}

export function initAdsFromPublicConfig(config: PublicConfig): void {
  adsConfig = {
    ads_enabled: config.ads_enabled,
    adsense_client: config.adsense_client,
    adsense_slot_quest: config.adsense_slot_quest,
    adsense_slot_home: config.adsense_slot_home,
  };
  applyAdsenseVerificationMeta(config.adsense_client);
  syncAdPreviewFromLocation();
}

/** AdSense site verification meta; runs even when ads_enabled is false. */
export function applyAdsenseVerificationMeta(client: string | null | undefined): void {
  const id = client?.trim();
  if (!id) return;
  let meta = document.querySelector('meta[name="google-adsense-account"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "google-adsense-account");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", id);
}

function ensureAdsScript(client: string): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]',
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("AdSense script failed")), { once: true });
      if (existing.dataset.loaded === "1") resolve();
      return;
    }
    const s = document.createElement("script");
    s.async = true;
    s.crossOrigin = "anonymous";
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
    s.dataset.loaded = "0";
    s.addEventListener(
      "load",
      () => {
        s.dataset.loaded = "1";
        resolve();
      },
      { once: true },
    );
    s.addEventListener("error", () => reject(new Error("AdSense script failed")), { once: true });
    document.head.appendChild(s);
  });
  return scriptPromise;
}

function pushAd(ins: HTMLElement): void {
  if (pushed.has(ins)) return;
  pushed.add(ins);
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
  } catch {
    /* ignore duplicate or blocked pushes */
  }
}

function scheduleAdFill(ins: HTMLElement, client: string): void {
  const run = () => {
    void ensureAdsScript(client).then(() => {
      if (document.contains(ins)) pushAd(ins);
    });
  };
  const w = globalThis as Window & typeof globalThis;
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(() => run(), { timeout: 2500 });
  } else {
    w.setTimeout(run, 0);
  }
}

function slotSizeKey(placement: AdPlacement): AdSlotSizeKey {
  return placement === "home" ? "home" : "quest";
}

function buildAdShell(placement: AdPlacement, label: string, preview: boolean): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = preview ? "mapus-ad mapus-ad--preview" : "mapus-ad";
  wrap.dataset.adPlacement = placement;

  const tag = document.createElement("span");
  tag.className = "mapus-ad-label";
  tag.textContent = label;
  wrap.append(tag);

  const slotWrap = document.createElement("div");
  slotWrap.className = "mapus-ad-slot";

  if (preview) {
    const fill = document.createElement("div");
    fill.className = "mapus-ad-preview-fill";
    fill.setAttribute("aria-hidden", "true");
    slotWrap.append(fill);
  } else {
    const ins = document.createElement("ins");
    ins.className = "adsbygoogle";
    ins.style.display = "block";
    slotWrap.append(ins);
  }

  wrap.append(slotWrap);
  return wrap;
}

function mountLiveAdSlot(placement: AdPlacement, label: string): HTMLElement | null {
  const cfg = adsConfig;
  if (!cfg || !adsServingReady(cfg)) return null;
  const client = cfg.adsense_client!;
  const slot = slotForPlacement(cfg, placement);
  if (!slot) return null;

  const wrap = buildAdShell(placement, label, false);
  const ins = wrap.querySelector<HTMLElement>("ins.adsbygoogle");
  if (!ins) return null;
  ins.setAttribute("data-ad-client", client);
  ins.setAttribute("data-ad-slot", slot);
  ins.setAttribute("data-ad-format", "auto");
  ins.setAttribute("data-full-width-responsive", "true");
  scheduleAdFill(ins, client);
  return wrap;
}

function mountPreviewAdSlot(placement: AdPlacement): HTMLElement {
  const sizeKey = slotSizeKey(placement);
  const label = `${t("ads.previewLabel")} · ${adSlotSizeLabel(sizeKey)}`;
  return buildAdShell(placement, label, true);
}

/**
 * Mount a responsive display ad unit. Returns null when ads are off (no DOM, no layout gap).
 * With `?adpreview=1` (session-persisted), renders dashed placeholders in the same containers.
 */
export function mountAdSlot(placement: AdPlacement, label: string): HTMLElement | null {
  if (readAdPreviewEnabled()) return mountPreviewAdSlot(placement);
  return mountLiveAdSlot(placement, label);
}

/** HTML for the proposed in-feed ad card after the 5th home list quest (preview only). */
export function homeFeedAdPreviewHtml(): string | null {
  if (!readAdPreviewEnabled()) return null;
  const label = `${t("ads.previewLabel")} · ${t("ads.previewProposed")} · ${AD_FEED_CARD_SIZE_LABEL}`;
  const escaped = label.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
  return `<div class="nearby-item-shell mapus-ad-feed-shell">
  <div class="mapus-ad mapus-ad--preview mapus-ad--feed" data-ad-placement="home_feed" role="presentation">
    <span class="mapus-ad-label">${escaped}</span>
    <div class="mapus-ad-slot">
      <div class="mapus-ad-preview-fill mapus-ad-preview-fill--feed" aria-hidden="true"></div>
    </div>
  </div>
</div>`;
}

export { adPreviewFromSearch, applyAdPreviewSession };
